import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Zap, Target, Brain, Calendar, Shield } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

const signupSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const TRIAL_DAYS = 14;

const features = [
  { icon: Target, label: "Unlimited goals, milestones & tasks" },
  { icon: Brain, label: "AI-powered coaching & insights" },
  { icon: Calendar, label: "Google Calendar sync & time budgets" },
  { icon: Zap, label: "Habit tracking, journal & meditation" },
  { icon: Shield, label: "No credit card required" },
];

export default function SignupPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (values: SignupFormValues) => {
      const res = await apiRequest("POST", "/api/auth/register-trial", {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Account created!",
        description: "Please log in to start your free trial.",
      });
      setLocation("/login");
    },
    onError: (error: any) => {
      // apiRequest throws an Error whose .message is already the server's error text
      setServerError(error?.message || "Registration failed. Please try again.");
    },
  });

  const onSubmit = (values: SignupFormValues) => {
    setServerError(null);
    registerMutation.mutate(values);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

        {/* Left column — value proposition */}
        <div className="text-center lg:text-left space-y-6">
          <div>
            <Link href="/" className="inline-flex items-center gap-2 text-primary font-semibold text-lg mb-6 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">O</span>
              </div>
              GoalVia
            </Link>
          </div>
          <div>
            <div className="inline-block bg-primary/10 text-primary text-sm font-semibold px-3 py-1 rounded-full mb-4">
              {TRIAL_DAYS}-Day Free Trial — No Credit Card Required
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
              Get full access to<br />
              <span className="text-primary">every feature</span>, free.
            </h1>
            <p className="mt-4 text-gray-600 dark:text-gray-300 text-lg">
              Start your {TRIAL_DAYS}-day trial with our Enterprise plan. Explore everything, then choose what fits you best.
            </p>
          </div>
          <ul className="space-y-3">
            {features.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>{label}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            After {TRIAL_DAYS} days, keep using the free plan or choose a paid plan that works for you.
          </p>
        </div>

        {/* Right column — signup form */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">Start your free trial</CardTitle>
            <CardDescription>
              Already have an account?{" "}
              <Link href="/" className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input placeholder="Jane" autoComplete="given-name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input placeholder="Smith" autoComplete="family-name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane@example.com" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="At least 8 characters" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Repeat your password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serverError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                    {serverError}
                    {serverError.toLowerCase().includes("already exists") && (
                      <span>
                        {" "}
                        <Link href="/" className="font-semibold underline hover:text-red-800">
                          Log in here
                        </Link>
                      </span>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold"
                  disabled={registerMutation.isPending}
                >
                  {registerMutation.isPending ? "Creating your account…" : `Start My ${TRIAL_DAYS}-Day Free Trial`}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  By signing up you agree to our{" "}
                  <a href="#" className="underline hover:text-primary">Terms of Service</a>
                  {" "}and{" "}
                  <a href="#" className="underline hover:text-primary">Privacy Policy</a>.
                </p>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
